import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Anchor, Lock, ArrowRight, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/20216716/pexels-photo-20216716.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";
const LOGO = "https://customer-assets-0z36b82j.emergentagent.net/job_3746c8ad-73a3-4cbb-87f3-7b3af248f392/artifacts/kwgzn05w_image.png";
const LOGO2 = "https://customer-assets-39nsmqrw.emergentagent.net/job_vessel-tracker-demo-1/artifacts/fa2sce7j_image.png";

export default function LoginPage() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [challenge, setChallenge] = useState(null); // { challengeId, emailMasked, devCode }
  const [code, setCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error("Access denied", { description: res.error });
    } else if (res.twofa) {
      setChallenge({ challengeId: res.challengeId, emailMasked: res.emailMasked, devCode: res.devCode });
      setStep("otp");
      setCode("");
      if (res.devCode) {
        toast.message("Verification required", { description: "Email delivery isn't set up for this address yet — your code is shown on screen." });
      } else {
        toast.success("Code sent", { description: `We emailed a 6-digit code to ${res.emailMasked}` });
      }
    } else {
      toast.success("Welcome back", { description: "Loading your briefing…" });
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await verifyOtp(challenge.challengeId, code.trim());
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error("Verification failed", { description: res.error });
    } else {
      toast.success("Verified", { description: "Loading your briefing…" });
    }
  };

  const handleResend = async () => {
    setError("");
    setLoading(true);
    const res = await resendOtp(challenge.challengeId);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error("Couldn't resend", { description: res.error });
    } else {
      setChallenge({ challengeId: res.challengeId, emailMasked: res.emailMasked, devCode: res.devCode });
      setCode("");
      if (res.devCode) {
        toast.message("New code issued", { description: "Your new code is shown on screen." });
      } else {
        toast.success("New code sent", { description: `We emailed a fresh code to ${res.emailMasked}` });
      }
    }
  };

  const backToLogin = () => {
    setStep("credentials");
    setError("");
    setCode("");
    setChallenge(null);
    setPassword("");
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background">
      {/* Left / hero */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden">
        <img src={HERO} alt="Container port at dusk" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/50" />

        <div className="relative z-10 flex items-center gap-3 px-10 pt-10">
          <img src={LOGO} alt="ARB 4x4 Accessories" className="h-9 bg-white rounded px-2 py-1 object-contain" />
          <span className="h-6 w-px bg-white/25" />
          <img src={LOGO2} alt="Scan Global Logistics" className="h-8 bg-white rounded px-2 py-1 object-contain" />
        </div>

        <div className="relative z-10 px-10 pb-14 max-w-xl animate-fade-up">
          <div className="font-mono-plex text-[11px] tracking-[0.3em] uppercase text-white/60 mb-5">
            Prepared 28 Aug 2026
          </div>
          <h1 className="font-display text-5xl xl:text-6xl leading-[1.02] tracking-tight text-white mb-6">
            SGL Value<br />Proposition to ARB
          </h1>
          <p className="text-white/70 text-base leading-relaxed max-w-md">
            Market Intelligence, Supply Chain and Commercial insights.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 border border-white/15 px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            <span className="font-mono-plex text-[11px] tracking-widest uppercase text-white/60">Private, invite-only access</span>
          </div>
        </div>
      </div>

      {/* Right / form */}
      <div className="flex items-center justify-center px-6 py-16 sm:px-12 relative">
        <div className="lg:hidden absolute top-8 left-6 flex items-center gap-3">
          <img src={LOGO} alt="ARB 4x4 Accessories" className="h-8 bg-white rounded px-2 py-1 object-contain" />
          <img src={LOGO2} alt="Scan Global Logistics" className="h-7 bg-white rounded px-1.5 py-1 object-contain" />
        </div>

        {step === "credentials" ? (
          <div className="w-full max-w-sm animate-fade-up">
            <div className="mb-9">
              <div className="font-mono-plex text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
                Secure access
              </div>
              <h2 className="font-display text-3xl tracking-tight mb-2.5">Sign in to your briefing</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Enter your approved credentials to view the market intelligence briefing.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <label htmlFor="email" className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  placeholder="name@sglarb.com.au"
                  className="w-full bg-secondary/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors"
                />
              </div>

              <div>
                <label htmlFor="password" className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  placeholder="••••••••••"
                  className="w-full bg-secondary/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors"
                />
              </div>

              {error && (
                <div data-testid="login-error" className="border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-red-400">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="group w-full bg-primary text-primary-foreground font-medium text-sm py-3.5 px-4 flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Verifying…" : "Sign in to your briefing"}
                {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
              </button>
            </form>

            <div className="mt-8 flex items-center gap-2 text-muted-foreground">
              <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span className="text-xs">Access is restricted to approved email addresses only.</span>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-sm animate-fade-up">
            <div className="mb-9">
              <div className="inline-flex items-center gap-2 font-mono-plex text-[11px] tracking-[0.3em] uppercase text-[#E3000F] mb-3">
                <ShieldCheck className="h-3.5 w-3.5" /> Two-factor verification
              </div>
              <h2 className="font-display text-3xl tracking-tight mb-2.5">Enter your code</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We sent a 6-digit verification code to <span className="text-foreground font-medium">{challenge?.emailMasked}</span>. Enter it below to continue.
              </p>
            </div>

            <form onSubmit={handleVerify} className="space-y-5" data-testid="otp-form">
              {challenge?.devCode && (
                <div data-testid="otp-devcode-notice" className="border border-amber-400/50 bg-amber-50 px-4 py-3 rounded-md">
                  <div className="font-mono-plex text-[10px] tracking-widest uppercase text-amber-700 mb-1">Email delivery not set up for this address yet</div>
                  <p className="text-xs text-amber-800/80 mb-2 leading-relaxed">Use this verification code to continue (shown here because a sending domain isn't verified in Resend yet):</p>
                  <div className="flex items-center gap-3">
                    <span data-testid="otp-devcode-value" className="font-mono-plex text-2xl tracking-[0.35em] font-semibold text-amber-900">{challenge.devCode}</span>
                    <button type="button" data-testid="otp-devcode-fill" onClick={() => setCode(challenge.devCode)} className="text-[11px] font-mono-plex uppercase tracking-widest border border-amber-500/50 text-amber-800 px-3 py-1.5 hover:bg-amber-100 transition-colors">Use code</button>
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="otp" className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">
                  Verification code
                </label>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  data-testid="otp-code-input"
                  placeholder="000000"
                  className="w-full bg-secondary/40 border border-border px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono-plex text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors"
                />
              </div>

              {error && (
                <div data-testid="otp-error" className="border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-red-400">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                data-testid="otp-verify-button"
                className="group w-full bg-primary text-primary-foreground font-medium text-sm py-3.5 px-4 flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Verifying…" : "Verify & sign in"}
                {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-between">
              <button onClick={backToLogin} data-testid="otp-back-button" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
              <button onClick={handleResend} disabled={loading} data-testid="otp-resend-button" className="text-xs text-[#E3000F] hover:underline disabled:opacity-50">
                Resend code
              </button>
            </div>

            <div className="mt-8 flex items-center gap-2 text-muted-foreground">
              <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span className="text-xs">Codes expire after 10 minutes for your security.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
